import React, { ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { BasicInput, ImageIcon, SecondaryButton, TertiaryButton, TextArea, Typography } from '@dev/ee-components';
import { ShowMediaManager } from '@/store/mediaManager/actions';
import { getPaidNewsletterCreatorStore, ModifyPaidNewsletterMetaData } from '@/store/paidNewsletterCreator/actions';
import { Step4EditContentWrapper, Step4EditContentColumns, InputLength, InputFieldWrapper } from './styles';
import { SocialPreview } from '../../components/SocialPreview';
import { SocialShareMediaManager } from '../../components/SocialShareMediaManager';

const PAGE_TITLE_LIMIT = 60;
const PAGE_DESCRIPTION_LIMIT = 160;

export const Step4EditContent = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { paidNewsletterObject, paidNewsletterMetaDataPayload, paidNewsletterMetaDataObject } = useSelector(getPaidNewsletterCreatorStore);

    const {
        pageTitle = '',
        pageDescription = '',
        imageUrl = ''
    } = { ...paidNewsletterMetaDataObject?.data?.socialPreview, ...paidNewsletterMetaDataPayload?.data?.socialPreview };

    const handleChangePageTitle = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const title = event?.currentTarget?.value || '';
        dispatch(ModifyPaidNewsletterMetaData({ data: { socialPreview: { pageTitle: title }} }));
    }

    const handleChangePageDescription = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const description = event?.currentTarget?.value || '';
        dispatch(ModifyPaidNewsletterMetaData({ data: { socialPreview: { pageDescription: description }} }));
    }

    const openMediaManager = () => {
        dispatch(ShowMediaManager());
    }

    const removeSocialImage = () => {
        dispatch(ModifyPaidNewsletterMetaData({ data: { socialPreview: { imageUrl: '' }} }));
    }

    return (
        <Step4EditContentWrapper>
            <Typography variant="body1">{t('lp_creator_social_description')}</Typography>
            <Step4EditContentColumns>
                <div>
                    <Typography variant="h4" style={{ marginBottom: 4 }}>
                        {t('lp_creator_social_imageheader')}
                    </Typography>
                    <Typography variant="body1" style={{ marginBottom: 16 }}>
                        {t('lp_creator_social_imagedescription')}
                    </Typography>
                    {!imageUrl ? (
                        <SecondaryButton 
                            text={t('lp_creator_social_addimage')}
                            style={{ marginBottom: 16 }}
                            startIcon={<ImageIcon />}
                            onClick={openMediaManager}
                        />
                    ) : (
                        <div>
                            <SecondaryButton 
                                text={t('lp_creator_social_changeimage')}
                                style={{ marginBottom: 16, marginRight: 16 }}
                                startIcon={<ImageIcon />}
                                onClick={openMediaManager}
                            />
                            <TertiaryButton
                                text={t('lp_creator_social_removeimage')}
                                style={{ marginBottom: 16 }}
                                onClick={removeSocialImage}
                            />
                        </div>
                    )}
                    <InputFieldWrapper>
                        <BasicInput 
                            id='social_share_page_title'
                            placeholder={t('lp_creator_social_pagetitle_placeholder')}
                            name='social_share_page_title'
                            label={t('lp_creator_social_pagetitle')}
                            value={pageTitle}
                            onChange={handleChangePageTitle}
                            inputProps={{
                                maxLength: PAGE_TITLE_LIMIT,
                            }} 
                        />
                    </InputFieldWrapper>
                    <InputLength>
                        {pageTitle?.length}/{PAGE_TITLE_LIMIT}
                    </InputLength>
                    <InputFieldWrapper>
                        <TextArea
                            id='social_share_page_description'
                            placeholder={t('lp_creator_social_pagedescription_placeholder')}
                            name='social_share_page_description'
                            label={t('lp_creator_social_pagedescription')}
                            rows={4}
                            maxInputLength={PAGE_DESCRIPTION_LIMIT}
                            value={pageDescription}
                            onChange={handleChangePageDescription}
                        />
                    </InputFieldWrapper>
                    <InputLength>
                        {pageDescription?.length}/{PAGE_DESCRIPTION_LIMIT}
                    </InputLength>
                </div>
                <div>
                    <SocialPreview 
                        imageUrl={imageUrl}
                        pageUrl={paidNewsletterObject?.ProductLanding?.LandingUrl ?? ''}
                        pageTitle={pageTitle}
                        pageDescription={pageDescription}
                        isEdit
                    />
                </div>
            </Step4EditContentColumns>
            <SocialShareMediaManager />
        </Step4EditContentWrapper>
    );
};
